let googleMapsLoader: Promise<void> | null = null;

declare global {
  interface Window {
    google?: any;
  }
}

async function waitFor(check: () => boolean, timeoutMs = 5000, intervalMs = 50): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (check()) return;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Google Maps did not finish loading in time");
}

export function loadGoogleMaps(options?: { libraries?: Array<"places" | "marker"> }): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();

  // Already loaded enough for maps + any requested libs
  const libs = options?.libraries ?? [];
  const hasMaps = !!window.google?.maps;
  const hasPlaces = !libs.includes("places") || !!window.google?.maps?.places;
  const hasMarker = !libs.includes("marker") || !!window.google?.maps?.marker;
  if (hasMaps && hasPlaces && hasMarker) return Promise.resolve();

  if (googleMapsLoader) return googleMapsLoader;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  if (!apiKey) {
    return Promise.reject(new Error("Missing VITE_GOOGLE_MAPS_API_KEY"));
  }

  googleMapsLoader = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-maps="places"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps script")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.setAttribute("data-google-maps", "places");
    script.async = true;
    script.defer = true;
    // loading=async is recommended by Google to avoid performance warnings
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  }).then(async () => {
    const google = (window as any).google;
    if (!google?.maps) {
      await waitFor(() => !!(window as any).google?.maps);
    }

    // Newer loading patterns require importing libraries for constructors to exist.
    if (typeof google?.maps?.importLibrary === "function") {
      // maps is required for google.maps.Map, Geocoder, etc.
      await google.maps.importLibrary("maps");
      if (libs.includes("places")) await google.maps.importLibrary("places");
      if (libs.includes("marker")) await google.maps.importLibrary("marker");
    } else {
      // Legacy: wait for constructors to exist
      await waitFor(() => typeof (window as any).google?.maps?.Map === "function");
      if (libs.includes("places")) {
        await waitFor(() => !!(window as any).google?.maps?.places);
      }
    }
  });

  return googleMapsLoader;
}

export function loadGoogleMapsPlaces(): Promise<void> {
  return loadGoogleMaps({ libraries: ["places"] });
}


